import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { usePhoneLimit, ShowMore } from '../../mobile';

/**
 * Accounts list — browse + search the unified accounts table.
 *
 * Read-only browser. Filters by status (prospect/active/churned),
 * region, and free-text name. Each row navigates to the Account 360
 * page at /accounts/:id.
 *
 * Visibility: gated by the same server-side flag as the Account 360
 * page (super_admin always allowed). When the page returns 403, we
 * show a friendly "not enabled yet" message instead of a raw error.
 */
export default function AccountsListPage() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  // Phones get the first 40 + "Show more" (resets on every new result set).
  const { shown, rest, more } = usePhoneLimit(accounts, accounts);
  const [status, setStatus] = useState('');
  const [region, setRegion] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notEnabled, setNotEnabled] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setNotEnabled(false);
    try {
      const res = await api.listAccounts360({ q, status, region, limit: 200 });
      setAccounts(res.accounts || []);
      setTotal(res.total || 0);
    } catch (e) {
      if (/not enabled/i.test(e.message)) setNotEnabled(true);
      else setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [q, status, region]);

  useEffect(() => {
    const t = setTimeout(load, 250); // debounce filter changes
    return () => clearTimeout(t);
  }, [load]);

  if (notEnabled) {
    return (
      <div className="admin-page">
        <div className="page-header"><h2>Accounts</h2></div>
        <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, padding: 16, color: '#92400e' }}>
          The unified Accounts view is not enabled yet. A super-admin can flip
          the <code>accounts_page_enabled</code> flag on the Migration Console
          when ready.
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2>Accounts</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            Unified CRM directory — prospects and customers. {total.toLocaleString()} total.
          </p>
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <input
          type="search"
          placeholder="Search name…"
          value={q}
          onChange={e => setQ(e.target.value)}
          style={{ flex: '1 1 240px', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1' }}
        />
        <select value={status} onChange={e => setStatus(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1' }}>
          <option value="">All statuses</option>
          <option value="active">Active customers</option>
          <option value="prospect">Prospects</option>
          <option value="churned">Churned</option>
        </select>
        <select value={region} onChange={e => setRegion(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1' }}>
          <option value="">All regions</option>
          <option value="Fort Worth">Fort Worth</option>
          <option value="Dallas">Dallas</option>
          <option value="Denton">Denton</option>
        </select>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Region</th>
                <th>City</th>
                <th>Management Co.</th>
                <th>ALN</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24, color: '#94a3b8' }}>Loading…</td></tr>}
              {!loading && accounts.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24, color: '#94a3b8' }}>No accounts match.</td></tr>
              )}
              {!loading && shown.map(a => (
                <tr key={a.id} onClick={() => navigate(`/accounts/${a.id}`)} style={{ cursor: 'pointer' }}>
                  <td style={{ fontWeight: 600 }}>{a.name}</td>
                  <td><StatusPill status={a.status} /></td>
                  <td>{a.region || '—'}</td>
                  <td>{a.city || '—'}</td>
                  <td style={{ fontSize: 12, color: '#475569' }}>{a.mgmt_company_name || '—'}</td>
                  <td><AlnPill match={a.aln_match_status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && <ShowMore rest={rest} onMore={more} />}
        </div>
      </div>
    </div>
  );
}

const STATUS_STYLES = {
  active:   { bg: '#10b98118', fg: '#059669', label: 'Active' },
  prospect: { bg: '#3b82f618', fg: '#2563eb', label: 'Prospect' },
  churned:  { bg: '#94a3b818', fg: '#64748b', label: 'Churned' },
};

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || { bg: '#94a3b818', fg: '#64748b', label: status || '—' };
  return (
    <span style={{
      padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
      background: s.bg, color: s.fg, textTransform: 'uppercase', letterSpacing: 0.4,
    }}>{s.label}</span>
  );
}

const ALN_STYLES = {
  matched:    { bg: '#10b98118', fg: '#059669', label: 'Matched' },
  manual:     { bg: '#7c3aed18', fg: '#6d28d9', label: 'Manual' },
  unmatched:  { bg: '#f59e0b18', fg: '#b45309', label: 'Unmatched' },
  disputed:   { bg: '#dc262618', fg: '#b91c1c', label: 'Disputed' },
};

function AlnPill({ match }) {
  const s = ALN_STYLES[match] || ALN_STYLES.unmatched;
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 700,
      background: s.bg, color: s.fg, letterSpacing: 0.4,
    }}>{s.label}</span>
  );
}
